import React from 'react';
import { Alert, Box, Divider, Tooltip, Typography } from '@mui/material';

import { getLoggedInUserOrganization } from '@paragon/login-tools';

import atticShapeIcon from '../../../../../../resources/ComponentShapeAttic.png';
import commonShapeIcon from '../../../../../../resources/ComponentShapeCommon.png';
import featurePatternShapeIcon from '../../../../../../resources/ComponentShapeFeaturePattern.png';
import gableShapeIcon from '../../../../../../resources/ComponentShapeGable.png';
import jackShapeIcon from '../../../../../../resources/ComponentShapeJack.png';
import piggybackShapeIcon from '../../../../../../resources/ComponentShapePiggyback.png';
import scissorsShapeIcon from '../../../../../../resources/ComponentShapeScissors.png';
import valleyShapeIcon from '../../../../../../resources/ComponentShapeValley.png';
import { chooseInsertionComponentType } from '../../../../../store/actions/insertComponentDesignActions';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import HeelSettings from '../../properties/HeelSettings';
import { LabeledBox } from '../../properties/LabeledBox';
import { ProfileCreator } from './profile-creator/ProfileCreator';
import { Length } from './shape-details/Length';

export function ShapeChooser() {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);
  const loggedInUserOrganization = useAppSelector(getLoggedInUserOrganization);

  if (insertingObjectInfo?.type !== 'componentDesign') throw new Error('Expected to be inserting a component design.');

  const currentShape = insertingObjectInfo.shape;

  const showG2Templates =
    loggedInUserOrganization === 'G2 National' ||
    loggedInUserOrganization === 'Paragon Component Systems' ||
    loggedInUserOrganization === 'Develop Organization A';

  return (
    <Box>
      <Box
        sx={{
          marginLeft: 3,
          marginTop: 2,
          paddingBottom: 2,
        }}
      >
        <Typography variant="h6">Draw a freeform profile from scratch:</Typography>

        <Length sx={{ marginTop: 3, marginBottom: -1 }} />

        <Alert severity="info" variant="outlined" sx={{ marginX: 1, marginY: 2, textWrap: 'wrap' }}>
          Draw profile lines in the scene on the left to define the shape of your component, or manually input lines
          using the controls below.
        </Alert>

        <ProfileCreator />

        <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <LabeledBox title="Left Heel">
            <HeelSettings side="Left" condensed />
          </LabeledBox>
          <LabeledBox title="Right Heel">
            <HeelSettings side="Right" condensed />
          </LabeledBox>
        </Box>
      </Box>

      <Divider />
      <Typography
        variant="h6"
        sx={{
          marginLeft: 3,
          marginTop: 2,
        }}
      >
        Or choose a shape below:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'Common'));
          }}
          isSelected={currentShape === 'Common'}
          imageSrc={commonShapeIcon}
          shapeName="Common"
        />
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'Gable'));
          }}
          isSelected={currentShape === 'Gable'}
          imageSrc={gableShapeIcon}
          shapeName="Gable"
        />
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'Jack'));
          }}
          isSelected={currentShape === 'Jack'}
          imageSrc={jackShapeIcon}
          shapeName="Jack"
        />
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'Scissors'));
          }}
          isSelected={currentShape === 'Scissors'}
          imageSrc={scissorsShapeIcon}
          shapeName="Scissors"
        />
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'Valley'));
          }}
          isSelected={currentShape === 'Valley'}
          imageSrc={valleyShapeIcon}
          shapeName="Valley"
        />
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'Piggyback'));
          }}
          isSelected={currentShape === 'Piggyback'}
          imageSrc={piggybackShapeIcon}
          shapeName="Piggyback"
        />
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', 'Attic'));
          }}
          isSelected={currentShape === 'Attic'}
          imageSrc={atticShapeIcon}
          shapeName="Attic"
          tooltip="Attic (Beta)"
        />
        <ShapeButton
          onClick={async () => {
            await dispatch(chooseInsertionComponentType('FloorTruss', 'FeaturePattern', 'default', 'FeaturePattern'));
          }}
          isSelected={currentShape === 'FeaturePattern'}
          imageSrc={featurePatternShapeIcon}
          shapeName="Feature Pattern"
        />
        {showG2Templates ? (
          <>
            <TextShapeButton
              onClick={async () => {
                await dispatch(chooseInsertionComponentType('RoofTruss', 'FeaturePattern', 'G2T-TC-TC', 'G2T-TC-TC'));
              }}
              isSelected={currentShape === 'G2T-TC-TC'}
            >
              G2T-TC-TC
            </TextShapeButton>
            <TextShapeButton
              onClick={async () => {
                await dispatch(chooseInsertionComponentType('RoofTruss', 'FeaturePattern', 'G2T-BC-BC', 'G2T-BC-BC'));
              }}
              isSelected={currentShape === 'G2T-BC-BC'}
            >
              G2T-BC-BC
            </TextShapeButton>
            <TextShapeButton
              onClick={async () => {
                await dispatch(chooseInsertionComponentType('RoofTruss', 'FeaturePattern', 'G2T-BC-TC', 'G2T-BC-TC'));
              }}
              isSelected={currentShape === 'G2T-BC-TC'}
            >
              G2T-BC-TC
            </TextShapeButton>
            <TextShapeButton
              onClick={async () => {
                await dispatch(chooseInsertionComponentType('RoofTruss', 'FeaturePattern', 'G2T-TC-BC', 'G2T-TC-BC'));
              }}
              isSelected={currentShape === 'G2T-TC-BC'}
            >
              G2T-TC-BC
            </TextShapeButton>
            <TextShapeButton
              onClick={async () => {
                await dispatch(chooseInsertionComponentType('RoofTruss', 'FeaturePattern', 'PFT', 'PFT'));
              }}
              isSelected={currentShape === 'PFT'}
            >
              PFT
            </TextShapeButton>
            <TextShapeButton
              onClick={async () => {
                await dispatch(chooseInsertionComponentType('RoofTruss', 'FeaturePattern', 'PFT-J', 'PFT-J'));
              }}
              isSelected={currentShape === 'PFT-J'}
            >
              PFT-J
            </TextShapeButton>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

interface TextShapeButtonProps {
  readonly isSelected: boolean;
  readonly onClick: () => void;
  readonly children: string;
}

function TextShapeButton({ isSelected, onClick, children }: TextShapeButtonProps) {
  return (
    <Box
      sx={[
        {
          cursor: 'pointer',
          width: 150,
          height: 82.5,
          position: 'relative',
          m: 1.25,
          bgcolor: 'canvas.main',
          borderRadius: '15px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        isSelected ? { outline: '3px solid', outlineColor: 'primary.main' } : null,
      ]}
      onClick={onClick}
    >
      {children}
    </Box>
  );
}

interface ShapeButtonProps {
  readonly isSelected: boolean;
  readonly onClick: () => void;
  readonly imageSrc: string;
  readonly shapeName: string;

  readonly fullWidth?: boolean;
  readonly tooltip?: string /** default: the shape name */;
}

function ShapeButton({ isSelected, onClick, imageSrc, shapeName, fullWidth, tooltip }: ShapeButtonProps) {
  return (
    <Tooltip title={tooltip ?? shapeName}>
      <Box
        component="img"
        sx={[
          fullWidth
            ? {
                width: '100%',
              }
            : {
                width: 150,
                height: 82.5,
              },

          {
            cursor: 'pointer',

            position: 'relative',
            m: 1.25,
            borderRadius: '15px',
          },
          isSelected ? { outline: '3px solid', outlineColor: 'primary.main' } : null,
        ]}
        src={imageSrc}
        alt={shapeName}
        onClick={onClick}
      />
    </Tooltip>
  );
}
